import { __assign, __rest } from "tslib";
import React, { createContext, useCallback, useMemo, useState, } from 'react';
import { useSelector } from 'react-redux';
import { LoadableComponentContextProvider, } from '@wix/da-hooks/pkg/useLoadableComponent';
import useEditor from '../DaEditorWrapper/hooks/useEditor';
// TODO: Move this type to CommentEditor instead?
import { CommentableTypes } from '../Comment/redux/types';
import { CommentEditorSource } from './redux/types';
import { useCommentEditor, } from './hooks/useCommentEditor';
import { useCommentDaEditorReadyHandler } from './hooks/useCommentDaEditorReadyHandler';
import DraftCommentAutoTrigger from './DraftCommentAutoTrigger';
import { getRequiresFallback } from '../DaEditorWrapper/redux/selectors';
export var EditorContext = createContext({
    api: null,
    state: null,
    toolbars: null,
    isReady: false,
    overrideEditorState: function () { },
    props: {},
});
var EditorContextProvider = function (_a) {
    var children = _a.children, onEditorReady = _a.onEditorReady, restOptions = __rest(_a, ["children", "onEditorReady"]);
    var _b = useState(false), isReady = _b[0], setIsReady = _b[1];
    var handleEditorReady = useCallback(function (toolbars, api) {
        setIsReady(true);
        onEditorReady && onEditorReady(toolbars, api);
    }, [onEditorReady]);
    var editor = useEditor(__assign({ onEditorReady: handleEditorReady }, restOptions));
    var memoizedEditorContext = useMemo(function () { return ({
        api: editor.editorApi,
        state: editor.editorState,
        toolbars: editor.editorToolbars,
        props: editor.editorProps,
        overrideEditorState: editor.overrideEditorState,
        isReady: isReady,
    }); }, [editor, isReady]);
    return (React.createElement(EditorContext.Provider, { value: memoizedEditorContext }, children));
};
export var CommentEditorContext = createContext({
    // Mostly gibberish defaults
    editorId: '',
    commentableItemId: '',
    commentableType: CommentableTypes.COMMENT_USER,
    isBusy: false,
    isFocusRequested: false,
    shouldUseFallback: false,
    isFallbackEmpty: true,
    editorSource: CommentEditorSource.Independent,
    isExpanded: false,
    expandEditor: function () { return null; },
    collapseEditor: function () { return null; },
    setIsFallbackEmpty: function () { return null; },
});
var CommentEditorContextProvider = function (_a) {
    var children = _a.children, commentBoxId = _a.commentBoxId, commentableItemId = _a.commentableItemId, commentableType = _a.commentableType, submitValidator = _a.submitValidator, useCommentEditorOptions = __rest(_a, ["children", "commentBoxId", "commentableItemId", "commentableType", "submitValidator"]);
    var values = useCommentEditor(commentBoxId, commentableItemId, commentableType, useCommentEditorOptions);
    var memoizedCommentEditorContext = useMemo(function () { return (__assign(__assign({}, values), { editorId: commentBoxId, commentableItemId: commentableItemId, commentableType: commentableType })); }, [values, commentableItemId, commentableType, commentBoxId]);
    return (React.createElement(CommentEditorContext.Provider, { value: memoizedCommentEditorContext }, children));
};
/**
 * Contains all the context providers we need for a comment editor implementation.
 *
 * Includes: `EditorContext`, `LoadableComponentContext`, and `CommentEditorContext`.
 */
export var CommentEditorProviders = function (_a) {
    var children = _a.children, onEditorChange = _a.onEditorChange, onEditorInitialContentReady = _a.onEditorInitialContentReady, onEditorReady = _a.onEditorReady, onFocus = _a.onFocus, onBlur = _a.onBlur, forceLoadOnMount = _a.forceLoadOnMount, commentBoxId = _a.commentBoxId, autoFocus = _a.autoFocus, submitValidator = _a.submitValidator, rest = __rest(_a, ["children", "onEditorChange", "onEditorInitialContentReady", "onEditorReady", "onFocus", "onBlur", "forceLoadOnMount", "commentBoxId", "autoFocus", "submitValidator"]);
    var shouldUseFallback = useSelector(getRequiresFallback);
    var onCommentDaEditorReadyHandler = useCommentDaEditorReadyHandler(commentBoxId, autoFocus);
    var handleEditorReady = useCallback(function (toolbars, api) {
        onCommentDaEditorReadyHandler(toolbars, api);
        onEditorReady && onEditorReady(shouldUseFallback ? {} : toolbars, api);
    }, [onCommentDaEditorReadyHandler, onEditorReady, shouldUseFallback]);
    var handleEditorChange = useCallback(function (newState) {
        onEditorChange && onEditorChange(newState);
    }, [onEditorChange]);
    return (React.createElement(EditorContextProvider, { onEditorReady: handleEditorReady, onEditorChange: handleEditorChange, onEditorInitialContentReady: onEditorInitialContentReady, onFocus: onFocus, onBlur: onBlur },
        React.createElement(LoadableComponentContextProvider, { forceLoadOnMount: forceLoadOnMount },
            React.createElement(CommentEditorContextProvider, __assign({ commentBoxId: commentBoxId, autoFocus: autoFocus, submitValidator: submitValidator }, rest),
                React.createElement(DraftCommentAutoTrigger, { editorId: commentBoxId }),
                children))));
};
//# sourceMappingURL=context.js.map